import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import BarLoaderComponent from 'webapp/components/bar-loader';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type InfolayerItemDatasetSync from 'webapp/models-custom/infolayer-item/dataset-sync';

interface Args {
	model: InfolayerItemDatasetSync;
	onClose: () => void;
}

export default class InfolayerTypeDatasetSyncComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;

	get cssClasses(): string {
		let classes = this.args.model.classes;
		if (this.args.model.isLoading) {
			classes = classes.replace('loading', '');
		}
		return classes;
	}

	@action
	createMetrics(): void {
		this.args.onClose();

		const sourceId = this.args.model.datasetSync.spaceAccessId;

		this.mmCustomQueriesService.openCreateNewPopup(
			'dataset_created_succesfully',
			undefined,
			sourceId,
		);
	}

	@action
	viewData(): void {
		this.navigationService.transitionToRoute(
			'datamanager.connected.datasets.view',
			this.args.model.datasetSync.spaceAccessId,
		);
		this.args.onClose();
	}
	<template>
		<div role="button" class="infolayer infolayer--type-dataset-sync {{this.cssClasses}}">
			<div>
				{{#if @model.isLoading}}
					<div class="info">
						<BarLoaderComponent />
						<p>
							Syncing data for “{{@model.datasetSync.title}}” is in progress. This can
							take a while to complete. We’ll notify you once it’s ready.
						</p>
					</div>
					<div class="controls2">
						<div class="controls2__right">
							<a class="close" {{on "click" @onClose}}>Dismiss</a>
						</div>
					</div>
				{{else if @model.isError}}
					<p>
						We've encountered the following error while generating your export
						<strong>"{{@model.datasetSync.title}}"</strong>:
						{{@model.errorText}}
					</p>
				{{else}}
					<div class="info">
						<img
							src={{@model.datasetSync.avatar}}
							alt="Dataset avatar"
							class="avatar-icon"
						/>
						<div>
							<span class="title">Dataset “{{@model.datasetSync.title}}” is ready!</span>
							<p>{{@model.datasetSync.description}}</p>
						</div>
					</div>
					<div class="controls2">
						<div class="controls2__right">
							<a class="close" {{on "click" @onClose}}>Dismiss</a>
							<SubmitButtonComponent
								@text="New metric"
								@theme="blue-outlined"
								@icon="Plus"
								@noDelay={{true}}
								@onTrigger={{this.createMetrics}}
							/>
							<SubmitButtonComponent
								@text="View data"
								@icon="Data"
								@noDelay={{true}}
								@onTrigger={{this.viewData}}
							/>
						</div>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
